import { CognitoRefreshToken, CognitoUser } from 'amazon-cognito-identity-js';
import { HttpError } from 'react-admin';
import { httpClient } from '../shared/httpClient';
import { getUserPool } from './Cognito';
import { UserFields, UserLocationTypes } from '../shared/constants/users';
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { parseJwt } from '@/src/shared/utils/jwtTokens';
import { FirebaseDataProvider } from './FirebaseDataProvider';
import { HomeCityType } from '../components/inputs/LocationPicker';

const DEFAULT_HOME_CITY = {
  [UserLocationTypes.Country]: 'United States',
  [UserLocationTypes.State]: '',
  [UserLocationTypes.City]: '',
  [UserLocationTypes.Location]: {
    latitude: 39.8283,
    longitude: -98.5795,
  },
};

export const FirebaseAuthProvider = () => {
  return {
    // called when the user attempts to log in
    login: (loginProps: { email: string; password: string }) =>
      new Promise((resolve, reject) => {
        const { email, password } = loginProps;
        signInWithEmailAndPassword(getAuth(), email, password)
          .then((data) => {
            console.log('login success ', data.user.uid);
            resolve(data);
          })
          .catch((error) => {
            console.log('login error ', error.code);
            let errorMessage = 'Login failed. Please try again.';

            switch (error.code) {
              case 'auth/invalid-login-credentials':
                errorMessage = 'Invalid email or password.';
                break;
              case 'auth/invalid-email':
                errorMessage = 'The email address is not valid.';
                break;
              case 'auth/operation-not-allowed':
                errorMessage =
                  'Email/password accounts are not enabled. Please contact support.';
                break;
              case 'auth/too-many-requests':
                errorMessage =
                  'Too many failed login attempts. Please try again later or reset your password.';
                break;
              case 'auth/network-request-failed':
                errorMessage = 'Network error. Please check your internet connection.';
                break;
              default:
                console.error('Login error:', error);
            }

            reject(new Error(errorMessage));
          });
      }),
    logout: async () => {
      localStorage.removeItem('lastUser');
      await signOut(getAuth());
      return Promise.resolve();
    },
    loginClub: async (clubAccountUid: string) => {
      const { data: token } = await httpsCallable<unknown, string>(
        getFunctions(),
        'users-token-generate'
      )({ toUid: clubAccountUid, storeImpersonatingEmployeeUid: true });
      localStorage.setItem('lastUser', getAuth().currentUser.uid);
      await signOut(getAuth());
      await signInWithCustomToken(getAuth(), token);
    },
    logoutClub: async () => {
      // Use getIdTokenResult() (async, like mobile app) to get fresh claims.
      // Fall back to localStorage.lastUser set by loginClub in case the token was refreshed
      // and the impersonating_employee_uid claim is no longer present.
      const tokenResult = await getAuth().currentUser?.getIdTokenResult();
      const impersonatingId =
        (tokenResult?.claims?.impersonating_employee_uid as string | undefined) ??
        localStorage.getItem('lastUser');
      if (!impersonatingId) {
        console.log('User not found');
        return;
      }
      localStorage.removeItem('lastUser');
      const { data: token } = await httpsCallable<unknown, string>(
        getFunctions(),
        'users-token-generate'
      )({ toUid: impersonatingId });
      await signOut(getAuth());
      await signInWithCustomToken(getAuth(), token);
    },
    getIdentity: async () => {
      try {
        const currentUser = getAuth().currentUser;
        if (!currentUser) {
          throw new Error('Not authorized');
        }

        const user = await FirebaseDataProvider.getUserByUid(currentUser.uid);

        if (!user) {
          const username =
            currentUser.displayName ||
            currentUser.email?.split('@')[0] ||
            'user';

          const userData = {
            uid: currentUser.uid,
            [UserFields.Email]: currentUser.email,
            [UserFields.Username]: username,
            [UserFields.HomeCity]: DEFAULT_HOME_CITY,
          };

          try {
            const instance = httpsCallable(getFunctions(), 'users-user-create');
            await instance(userData);
            const newUser = await FirebaseDataProvider.getUserByUid(currentUser.uid);
            if (!newUser) {
              throw new Error('User not found');
            }
            return newUser;
          } catch (error) {
            console.error('Error creating default user:', error);
            await getAuth().signOut();
            window.location.assign('/login?error=This user does not exist');
            return null;
          }
        }
        return user;
      } catch (e) {
        console.error('Error in getIdentity:', e);
        await getAuth().signOut();
        window.location.assign('/login?error=Not logged in');
        return null;
      }
    },
    // getClubIdentity: async () => {
    //   const user = await getDocs(
    //     query(
    //       collection(firebaseStorage, 'users'),
    //       where('uid', '==', getAuth().currentUser.uid)
    //     )
    //   );
    //   return user.docs[0] ? { id: user.docs[0].id, ...user.docs[0].data() } : null;
    // },
    // called when the user navigates to a new location, to check for authentication
    checkAuth: ({ message, status, body }: any): Promise<any> =>
      new Promise(async (resolve, reject) => {
        if (getAuth().currentUser?.uid) {
          const userId = await getAuth().currentUser.getIdTokenResult();
          resolve(userId);
        } else {
          reject({ message: false, error: new Error('Not authorized') });
        }
      }),
    // called when the API returns an error
    checkError: ({ status }: { status: number }) => {
      // todo: add if additional security needed
      // if (status === 401 || status === 403) {
      //   const user = getUserPool().getCurrentUser();
      //   if (user) {
      //     clearCache();
      //     user.signOut();
      //   }
      //   return Promise.reject({
      //     redirectTo:
      //       '/login?error=Access to this resource is restricted. We have ended your session for security reasons. Please log in again',
      //   });
      // }
      console.error('cognito error: ', status);
      return Promise.resolve();
    },
    getPermissions: async () => {
      return {};
    },
    signup: (signupProps: {
      [UserFields.Email]: string;
      [UserFields.Username]: string;
      [UserFields.PhoneNumber]: string;
      [UserLocationTypes.Country]: string;
      [UserLocationTypes.State]: string;
      [UserLocationTypes.City]: string;
      [UserLocationTypes.Location]: HomeCityType;
      password: string;
    }) =>
      new Promise((resolve, reject) => {
        createUserWithEmailAndPassword(
          getAuth(),
          signupProps[UserFields.Email],
          signupProps.password
        )
          .then(async (userCredentials) => {
            const uid = userCredentials.user.uid;

            const userData = {
              uid,
              [UserFields.Username]:
                signupProps[UserFields.Username].trim() || `User ${uid.slice(0, 5)}`,
              [UserFields.Email]: signupProps[UserFields.Email],
              [UserFields.PhoneNumber]: signupProps[UserFields.PhoneNumber],
              [UserFields.HomeCity]: {
                [UserLocationTypes.Country]: signupProps[UserLocationTypes.Country],
                [UserLocationTypes.State]: signupProps[UserLocationTypes.State],
                [UserLocationTypes.City]: signupProps[UserLocationTypes.City],
                [UserLocationTypes.Location]: signupProps[UserLocationTypes.Location],
              },
            };

            try {
              const instance = httpsCallable(getFunctions(), 'users-user-create');
              await instance(userData);

              const user = await FirebaseDataProvider.getUserByUid(uid);
              if (!user) {
                throw new Error('User not found');
              }
              resolve(user);
            } catch (error) {
              // If Firestore storage fails, we should delete the Firebase Auth user
              try {
                await userCredentials.user.delete();
              } catch (deleteError) {
                console.error(
                  'Failed to delete user after Firestore error:',
                  deleteError
                );
              }
              console.error('Failed to store user information:', error);
              reject(new Error('Failed to store user information'));
            }
          })
          .catch((error) => {
            let errorMessage = 'Registration failed. Please try again.';

            switch (error.code) {
              case 'auth/email-already-in-use':
                errorMessage = 'User with this email already exists!';
                break;
              case 'auth/invalid-email':
                errorMessage = 'Email address is invalid!';
                break;
              case 'auth/weak-password':
                errorMessage = 'Password is too weak. Please use a stronger password.';
                break;
              case 'auth/operation-not-allowed':
                errorMessage =
                  'Email/password accounts are not enabled. Please contact support.';
                break;
              default:
                console.error('Registration error:', error);
            }

            reject(new Error(errorMessage));
          });
      }),
    confirmEmail: ({ userId, code }: { userId: string; code: string }) =>
      new Promise((resolve, reject) => {
        const url = `/users/${userId}/confirm/${code}`;
        httpClient(url, {
          method: 'PUT',
        })
          .then(({ json }) => resolve(json))
          .catch((err) => {
            if (err.message === 'Expired confirmation code provided') {
              reject(new HttpError(err.message, err.status));
            }
            if (err.message === 'Invalid confirmation code provided') {
              reject(new HttpError(err.message, err.status));
            }
            reject(
              new HttpError('Unable to confirm your email. Please try again', err.status)
            );
          });
      }),
    forgotPassword: (email: string): Promise<any> =>
      new Promise((resolve, reject) => {
        try {
          resolve(sendPasswordResetEmail(getAuth(), email));
        } catch (error: any) {
          if (error.code === 'auth/user-not-found') {
            reject(new Error('User not found'));
          }
          reject(new Error(error.message));
        }
      }),
    authByTokens: (refreshToken: string, username: string) =>
      new Promise((resolve, reject) => {
        if (!username) {
          reject('No username');
        }
        if (!refreshToken) {
          reject('No refresh token');
        }
        const user = new CognitoUser({
          Username: username,
          Pool: getUserPool(),
        });
        const cognitoRefreshToken = new CognitoRefreshToken({
          RefreshToken: refreshToken,
        });
        if (user) {
          user.refreshSession(cognitoRefreshToken, (err, session) => {
            if (err) {
              console.log(err);
              reject();
            } else {
              console.log('TOKEN SUCCESSFULLY UPDATED');
              resolve(session);
            }
          });
        }
      }),
  };
};
